import React, { useContext, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Card,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledTooltip,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import classnames from "classnames";
import { ISurveyResult } from "../../app/models/dto";
import surveyQuestionType from "../../app/models/survey";
import GenericModal from "../common/GenericModal";
import { RootStoreContext } from "../../app/stores/rootStore";
import classNames from "classnames";
import SurveyShareButton from "../buttons/SurveyShareButton";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { translationHookOptions } from "../../app/localization/localization";

export interface SurveyParams {
  surveyResultDetails: ISurveyResult;
}

interface IFreeAnswerContent {
  user: string;
  answer: string;
}

const SurveyResults: React.FC<SurveyParams> = ({ surveyResultDetails }) => {
  const [activeTab, setActiveTab] = useState(
    surveyResultDetails.surveyTypeId !== surveyQuestionType.Free ? "1" : "2"
  );
  const [freeAnswerModalContent, setFreeAnswerModalContent] =
    useState<IFreeAnswerContent | null>(null);
  const rootStore = useContext(RootStoreContext);
  const { view, setView, setSurveyId, surveyAnswersDetail, surveyId } =
    rootStore.surveyStore;
  const { theme } = rootStore.commonStore;
  const [t] = useTranslation(["surveys"], translationHookOptions);

  const toggle = (tab: any) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const columnsTotal = [
    {
      dataField: "response",
      text: t("results_table_answers_column"),
    },
    {
      dataField: "numberVote",
      text: t("results_table_votes_column"),
    },
    {
      dataField: "percentualVote",
      text: t("results_table_percent_column"),
      hidden: surveyResultDetails.surveyTypeId === surveyQuestionType.Rating,
    },
    {
      dataField: "users",
      text: t("results_table_users_column"),
    },
  ];

  const voteColumnFormatter = (
    cell: any,
    row: any,
    rowIndex: any,
    extra: any
  ) => {
    return surveyResultDetails.surveyTypeId !== surveyQuestionType.Free ? (
      <>{cell}</>
    ) : (
      <>
        <Button
          id={`btnViewAnswer_${rowIndex}`}
          color="link"
          onClick={() =>
            setFreeAnswerModalContent({ user: row.user, answer: cell })
          }
        >
          <i className="fas fa-eye fa-lg text-default"></i>
        </Button>
        <UncontrolledTooltip
          delay={0}
          placement="top"
          target={`btnViewAnswer_${rowIndex}`}
        >
          {t("results_view_answer")}
        </UncontrolledTooltip>
      </>
    );
  };

  const columnsSingle = [
    {
      dataField: "user",
      text: t("results_table_users_column"),
    },
    {
      dataField: "date",
      text: t("results_table_date_column"),
      formatter: (cell: string) => moment(cell).format("DD/MM/YYYY HH:mm"),
    },
    {
      dataField: "votes",
      text: t("results_table_single_answer_column"),
      formatter: voteColumnFormatter,
    },
  ];

  const setAnswerView = () => {
    if (view === "result") {
      setView("vote");
    } else {
      setView("result");
    }
  };

  const goBack = () => {
    setView("list");
    setSurveyId(0);
  };

  return (
    <div>
      <Row>
        <Col sm={3} xl={2} className="">
          <Button
            onClick={() => goBack()}
            className={classNames(
              "surveyButton backButton",
              `${theme!.buttonColor} ${theme!.buttonBorderColor} ${
                theme!.buttonTextColor
              }`
            )}
          >
            <span className="btn-inner--icon">
              <i className="fas fa-arrow-left" />
            </span>
            <span className="btn-inner--text">
              {" "}
              {t("back", { ns: "translation" })}
            </span>
          </Button>
        </Col>
      </Row>
      <Card className=" mt-4 voteCard">
        <Row className="justify-content-center mb-2 mt-2 mr-1 ml-1">
          <Col xs={6}>
            <h4 className="surveyQuestion">
              {surveyAnswersDetail?.survey.questionsLanguages.question}
            </h4>
          </Col>
          <Col xs={3}>
            <Row>
              <Col xs={12}>
                <h5 className="text-center surveyText">
                  {t("result_votes_label")}
                  {surveyResultDetails.quorumDetails.numberVotes}/
                  {surveyResultDetails.quorumDetails.numberUsers}
                </h5>
              </Col>
            </Row>
            <Row>
              <Col xs={12} xl={8} className="mx-auto">
                <Button
                  id="switch"
                  className={classNames(
                    "surveyButton w-100",
                    `${theme!.buttonColor} ${theme!.buttonBorderColor} ${
                      theme!.buttonTextColor
                    }`
                  )}
                  onClick={setAnswerView}
                  disabled={
                    surveyResultDetails.permissions.canVote ||
                    surveyResultDetails.permissions.hasVote
                      ? false
                      : true
                  }
                >
                  {t("survey_operation_vote")}
                </Button>
              </Col>
            </Row>
          </Col>
          <Col xs={3}>
            <Row>
              <Col xs={12}>
                <h5 className="text-center surveyText">
                  {t("result_quorum_label")}
                  {surveyResultDetails.quorumDetails.quorumVoters}
                </h5>
              </Col>
            </Row>
            <Row>
              <Col xs={12} xl={8} className="mx-auto">
                <SurveyShareButton
                  surveyId={surveyId}
                  permissions={surveyResultDetails.permissions}
                ></SurveyShareButton>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      <hr></hr>
      <div className="">
        <Nav tabs className="navQuestion">
          {surveyResultDetails.surveyTypeId !== surveyQuestionType.Free && (
            <NavItem>
              <NavLink
                href="#"
                className={classnames("surveyNavLinkResponse surveyText", {
                  active: activeTab === "1",
                })}
                onClick={() => {
                  toggle("1");
                }}
              >
                {t("total_votes_tab")}
              </NavLink>
            </NavItem>
          )}
          <NavItem>
            <NavLink
              href="#"
              className={classnames("surveyNavLinkResponse surveyText", {
                active: activeTab === "2",
              })}
              onClick={() => {
                toggle("2");
              }}
            >
              {t("individual_votes_tab")}
            </NavLink>
          </NavItem>
        </Nav>
        {surveyResultDetails ? (
          <TabContent activeTab={activeTab} className="tabQuestion tabResponse">
            <TabPane tabId="1" className="tabPaneSurvey">
              <Card className="mx-4 mb-2 voteCard">
                <BootstrapTable
                  classes="align-items-center mb-0"
                  filterPosition="top"
                  bootstrap4={true}
                  bordered={false}
                  wrapperClasses="table-responsive"
                  columns={columnsTotal}
                  keyField="date"
                  data={surveyResultDetails?.listResultTotal.totalResults}
                />
              </Card>
            </TabPane>
            <TabPane tabId="2">
              <Card className="mx-4 mb-2 voteCard">
                <BootstrapTable
                  classes="align-items-center mb-0"
                  filterPosition="top"
                  bootstrap4={true}
                  bordered={false}
                  wrapperClasses="table-responsive"
                  columns={columnsSingle}
                  keyField="date"
                  data={surveyResultDetails?.listResultSingle.singleResults}
                />
              </Card>
            </TabPane>
          </TabContent>
        ) : null}
      </div>
      <GenericModal
        isOpen={freeAnswerModalContent ? true : false}
        toggle={() => setFreeAnswerModalContent(null)}
        title={`Risposta di ${
          freeAnswerModalContent ? freeAnswerModalContent.user : ""
        }`}
        content={`${
          freeAnswerModalContent ? freeAnswerModalContent.answer : ""
        }`}
        size="md"
      />
    </div>
  );
};

export default observer(SurveyResults);
